import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Download, Trash2, Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import {
  getBackups, createBackup, deleteBackup, downloadBackup, BackupRequiresTotpError, sendActionEmailCode,
  getBackupSchedules, createBackupSchedule, updateBackupSchedule, deleteBackupSchedule,
  type Backup, type BackupSchedule, type BackupType, type BackupDestination, type TwoFactorMethod,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { formatDatetime } from '../utils/dates';
import TotpPrompt from '../components/TotpPrompt';

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const TYPE_LABEL: Record<BackupType, string> = { data: 'Kun data', full: 'Fuld' };
const STATUS_LABEL = {
  queued:  { label: 'I kø',     cls: 'bg-cream-200 text-ink-700',     Icon: Clock },
  running: { label: 'Kører',    cls: 'bg-sage-100 text-sage-700',     Icon: Loader2 },
  done:    { label: 'Klar',     cls: 'bg-sage-100 text-sage-900',     Icon: CheckCircle2 },
  failed:  { label: 'Fejlet',   cls: 'bg-rose-100 text-rose-700',     Icon: XCircle },
} as const;

export default function BackupPage() {
  const { logout } = useAuth();
  const [backups, setBackups]     = useState<Backup[]>([]);
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [loading, setLoading]     = useState(true);

  // Ny backup form
  const [newType, setNewType]               = useState<BackupType>('data');
  const [newDestination, setNewDestination] = useState<BackupDestination>('bunny');
  const [creating, setCreating]             = useState(false);

  // Schedule form
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  // Confirm-delete state
  const [confirmDeleteBackup, setConfirmDeleteBackup]     = useState<number | null>(null);
  const [confirmDeleteSchedule, setConfirmDeleteSchedule] = useState<number | null>(null);

  // 2FA-prompt state — sættes når download kræver 2FA. Methods kommer fra backend.
  const [totpPromptForBackup, setTotpPromptForBackup] = useState<Backup | null>(null);
  const [totpPromptMethods, setTotpPromptMethods] = useState<TwoFactorMethod[]>([]);

  const reload = useCallback(async () => {
    setLoading(true);
    const [b, s] = await Promise.all([getBackups(), getBackupSchedules()]);
    setBackups(b);
    setSchedules(s);
    setLoading(false);
  }, []);

  // Letvægts re-fetch under polling — kun backups, ingen loading-flip
  const reloadBackupsOnly = useCallback(async () => {
    const b = await getBackups();
    setBackups(b);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Poll mens der er aktive backups (queued/running). Schedule-listen ændres ikke
  // under en backup-kørsel, så vi henter kun backups + flipper ikke loading-state.
  useEffect(() => {
    const active = backups.some(b => b.status === 'queued' || b.status === 'running');
    if (!active) return;
    const id = setInterval(() => { reloadBackupsOnly(); }, 3000);
    return () => clearInterval(id);
  }, [backups, reloadBackupsOnly]);

  // Hold "Fuld → kun download" konsistent
  useEffect(() => {
    if (newType === 'full' && newDestination !== 'download') setNewDestination('download');
  }, [newType, newDestination]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createBackup({ type: newType, destination: newDestination });
      await reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Backup-trigger fejlede');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (b: Backup) => {
    try {
      await downloadBackup(b.id, b.filename);
      if (b.destination === 'download') await reload();
    } catch (err) {
      if (err instanceof BackupRequiresTotpError) {
        setTotpPromptForBackup(b);
        setTotpPromptMethods(err.methods);
        // Hvis kun email er aktiv: udløs en kode med det samme
        if (err.methods.length === 1 && err.methods[0] === 'email') {
          await sendActionEmailCode().catch(() => null);
        }
        return;
      }
      alert(err instanceof Error ? err.message : 'Download fejlede');
    }
  };

  const handleTotpDownload = async (code: string) => {
    if (!totpPromptForBackup) return;
    const b = totpPromptForBackup;
    await downloadBackup(b.id, b.filename, code);  // kaster ved forkert kode → TotpPrompt viser fejl
    setTotpPromptForBackup(null);
    setTotpPromptMethods([]);
    if (b.destination === 'download') await reload();
  };

  const handleDeleteBackup = async (id: number) => {
    await deleteBackup(id);
    setConfirmDeleteBackup(null);
    await reload();
  };

  const handleDeleteSchedule = async (id: number) => {
    await deleteBackupSchedule(id);
    setConfirmDeleteSchedule(null);
    await reload();
  };

  const toggleSchedule = async (s: BackupSchedule) => {
    await updateBackupSchedule(s.id, { enabled: !s.enabled });
    await reload();
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <a href="/hub" className="inline-flex items-center gap-1.5 text-sm text-sage-700 hover:text-sage-900 mb-2">
            <ArrowLeft size={14} /> Tilbage til Hub
          </a>
          <h1 className="page-title">Backup</h1>
          <p className="text-sm text-ink-500 mt-1">System-backup (Hub/superadmin)</p>
        </div>
        <button onClick={logout} className="text-sm text-ink-500 hover:text-ink-900">Log ud</button>
      </div>

      {/* ───── Tag ny backup ───── */}
      <section className="mb-8">
        <h2 className="text-base font-semibold text-ink-900 mb-3">Tag ny backup</h2>
        <div className="list-card p-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div>
              <label className="label">Type</label>
              <div className="flex gap-2">
                {(['data', 'full'] as BackupType[]).map(t => (
                  <button key={t} onClick={() => setNewType(t)}
                    className={`btn-toggle ${newType === t ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}>
                    {TYPE_LABEL[t]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-ink-500 mt-1.5">
                {newType === 'data'
                  ? 'Kun DB-dump (~MB). Hurtig — kan køres ofte.'
                  : 'DB + bil-billeder + system-filer (.env, Nginx, SSL). Stor — kun til download.'}
              </p>
            </div>
            <div>
              <label className="label">Gem hvor</label>
              <div className="flex gap-2">
                {(['bunny', 'download'] as BackupDestination[]).map(d => (
                  <button key={d}
                    onClick={() => setNewDestination(d)}
                    disabled={newType === 'full' && d === 'bunny'}
                    className={`btn-toggle ${newDestination === d ? 'btn-toggle-active' : 'btn-toggle-inactive'}
                      ${newType === 'full' && d === 'bunny' ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    {d === 'bunny' ? 'Online (Bunny)' : 'Download nu'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-ink-500 mt-1.5">
                {newDestination === 'bunny'
                  ? 'Uploades til krypteret backup-zone på Bunny.'
                  : 'Klar på serveren til engangs-download. Slettes efter hentning.'}
              </p>
            </div>
            <button onClick={handleCreate} disabled={creating} className="btn btn-primary">
              {creating ? 'Starter…' : 'Tag backup'}
            </button>
          </div>
        </div>
      </section>

      {/* ───── Planlagte backups ───── */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-ink-900">Planlagte backups</h2>
          <button onClick={() => setShowScheduleForm(s => !s)} className="btn btn-secondary text-sm py-1.5 px-3 inline-flex items-center gap-1.5">
            <Plus size={14} /> Tilføj
          </button>
        </div>

        {showScheduleForm && (
          <NewScheduleForm
            onSaved={() => { setShowScheduleForm(false); reload(); }}
            onCancel={() => setShowScheduleForm(false)}
          />
        )}

        <div className="list-card">
          {schedules.length === 0 ? (
            <div className="p-8 text-center text-ink-500 text-sm">Ingen planlagte backups</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="th">Type</th>
                  <th className="th">Frekvens</th>
                  <th className="th">Tid</th>
                  <th className="th">Næste kørsel</th>
                  <th className="th">Status</th>
                  <th className="th"></th>
                </tr>
              </thead>
              <tbody>
                {schedules.map(s => (
                  <tr key={s.id} className="row-hover">
                    <td className="px-4 py-3 text-ink-700">{TYPE_LABEL[s.type]}</td>
                    <td className="px-4 py-3 text-ink-700">{describeFrequency(s)}</td>
                    <td className="px-4 py-3 text-ink-700 font-mono text-xs">{String(s.hour).padStart(2,'0')}:{String(s.minute).padStart(2,'0')}</td>
                    <td className="px-4 py-3 text-ink-500 text-xs">{formatDatetime(s.next_run_at)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSchedule(s)}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.enabled ? 'bg-sage-100 text-sage-900' : 'bg-cream-200 text-ink-500'}`}>
                        {s.enabled ? 'Aktiv' : 'Pause'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {confirmDeleteSchedule === s.id ? (
                        <span className="flex items-center gap-2 justify-end">
                          <button onClick={() => handleDeleteSchedule(s.id)} className="text-xs text-rose-700 hover:underline font-medium">Slet</button>
                          <button onClick={() => setConfirmDeleteSchedule(null)} className="text-xs text-ink-300 hover:text-ink-700">×</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmDeleteSchedule(s.id)} className="text-ink-300 hover:text-rose-700">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* ───── Eksisterende backups ───── */}
      <section>
        <h2 className="text-base font-semibold text-ink-900 mb-3">Eksisterende backups</h2>
        <div className="list-card">
          {loading ? (
            <div className="p-12 text-center text-ink-500">…</div>
          ) : backups.length === 0 ? (
            <div className="p-12 text-center text-ink-500">Ingen backups endnu</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="th">Dato</th>
                  <th className="th">Type</th>
                  <th className="th">Placering</th>
                  <th className="th !text-right">Størrelse</th>
                  <th className="th">Status</th>
                  <th className="th"></th>
                </tr>
              </thead>
              <tbody>
                {backups.map(b => {
                  const s = STATUS_LABEL[b.status];
                  return (
                    <tr key={b.id} className="row-hover">
                      <td className="px-4 py-3 text-ink-700 whitespace-nowrap">
                        {formatDatetime(b.created_at)}
                        {b.is_scheduled && <span className="ml-1.5 text-xs text-ink-300">(auto)</span>}
                      </td>
                      <td className="px-4 py-3 text-ink-700">{TYPE_LABEL[b.type]}</td>
                      <td className="px-4 py-3 text-xs text-ink-500">
                        {b.destination === 'bunny'
                          ? <span className="inline-flex items-center gap-1">Bunny</span>
                          : <span>{b.status === 'done' ? 'På server (engangs)' : 'Download'}</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-ink-900 text-xs">{formatBytes(b.size_bytes)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>
                          <s.Icon size={11} className={b.status === 'running' ? 'animate-spin' : ''} />
                          {s.label}
                        </span>
                        {b.status === 'failed' && b.error_msg && (
                          <div className="text-xs text-rose-700 mt-1 max-w-xs truncate" title={b.error_msg}>{b.error_msg}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {confirmDeleteBackup === b.id ? (
                          <span className="flex items-center gap-2 justify-end">
                            <button onClick={() => handleDeleteBackup(b.id)} className="text-xs text-rose-700 hover:underline font-medium">Slet</button>
                            <button onClick={() => setConfirmDeleteBackup(null)} className="text-xs text-ink-300 hover:text-ink-700">×</button>
                          </span>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            {b.status === 'done' && (b.destination === 'bunny' || b.destination === 'download') && (
                              <button onClick={() => handleDownload(b)} title="Download"
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-sage-100 text-sage-700 hover:bg-sage-300 hover:text-cream-50 transition-colors">
                                <Download size={13} />
                              </button>
                            )}
                            {b.status !== 'queued' && b.status !== 'running' && (
                              <button onClick={() => setConfirmDeleteBackup(b.id)} title="Slet"
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-ink-300 hover:text-rose-700 hover:bg-rose-100 transition-colors">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {totpPromptForBackup && (
        <TotpPrompt
          title="2FA-bekræftelse for download"
          methods={totpPromptMethods}
          onSubmit={handleTotpDownload}
          onSendEmailCode={totpPromptMethods.includes('email') ? sendActionEmailCode : undefined}
          onCancel={() => { setTotpPromptForBackup(null); setTotpPromptMethods([]); }}
        />
      )}
    </main>
  );
}

// ───── Hjælpere ─────────────────────────────────────────────────────────────

const DOW = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];

function describeFrequency(s: BackupSchedule): string {
  if (s.day_of_week != null) return `Ugentlig (${DOW[s.day_of_week]})`;
  if (s.day_of_month != null) return `Månedlig (d. ${s.day_of_month})`;
  if (s.frequency_minutes >= 1440) return 'Dagligt';
  if (s.frequency_minutes >= 60) return `Hver ${s.frequency_minutes / 60} time(r)`;
  return `Hvert ${s.frequency_minutes}. minut`;
}

// ───── Ny schedule-form ─────────────────────────────────────────────────────

function NewScheduleForm({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const [type, setType]                 = useState<BackupType>('data');
  const [destination, setDestination]   = useState<BackupDestination>('bunny');
  const [mode, setMode]                 = useState<'daily' | 'weekly' | 'monthly' | 'interval'>('daily');
  const [hour, setHour]                 = useState(3);
  const [minute, setMinute]             = useState(0);
  const [dow, setDow]                   = useState(0);
  const [dom, setDom]                   = useState(1);
  const [intervalHours, setIntervalHours] = useState(6);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    if (type === 'full' && destination !== 'download') setDestination('download');
  }, [type, destination]);

  const save = async () => {
    setSaving(true);
    try {
      const payload: Partial<BackupSchedule> = {
        type, destination, hour, minute,
        frequency_minutes:
          mode === 'daily'    ? 1440 :
          mode === 'weekly'   ? 10080 :
          mode === 'monthly'  ? 43200 :
                                intervalHours * 60,
        day_of_week:  mode === 'weekly'  ? dow : null,
        day_of_month: mode === 'monthly' ? dom : null,
        enabled: true,
      };
      await createBackupSchedule(payload);
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="list-card p-5 mb-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="label">Type</label>
          <div className="flex gap-2">
            {(['data', 'full'] as BackupType[]).map(t => (
              <button key={t} onClick={() => setType(t)}
                className={`btn-toggle ${type === t ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}>
                {t === 'data' ? 'Kun data' : 'Fuld'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="label">Gem hvor</label>
          <div className="flex gap-2">
            {(['bunny', 'download'] as BackupDestination[]).map(d => (
              <button key={d} onClick={() => setDestination(d)}
                disabled={type === 'full' && d === 'bunny'}
                className={`btn-toggle ${destination === d ? 'btn-toggle-active' : 'btn-toggle-inactive'}
                  ${type === 'full' && d === 'bunny' ? 'opacity-40 cursor-not-allowed' : ''}`}>
                {d === 'bunny' ? 'Online' : 'Download'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="label">Frekvens</label>
        <div className="flex flex-wrap gap-2">
          {(['daily', 'weekly', 'monthly', 'interval'] as const).map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={`btn-toggle ${mode === m ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}>
              {({ daily: 'Dagligt', weekly: 'Ugentligt', monthly: 'Månedligt', interval: 'Hver X timer' } as const)[m]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {mode === 'weekly' && (
          <div>
            <label className="label">Ugedag</label>
            <select className="filter-input w-full" value={dow} onChange={e => setDow(Number(e.target.value))}>
              {DOW.map((d, i) => <option key={d} value={i}>{d}dag</option>)}
            </select>
          </div>
        )}
        {mode === 'monthly' && (
          <div>
            <label className="label">Dag i måneden</label>
            <select className="filter-input w-full" value={dom} onChange={e => setDom(Number(e.target.value))}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}.</option>)}
            </select>
          </div>
        )}
        {mode === 'interval' ? (
          <div>
            <label className="label">Hver X timer</label>
            <select className="filter-input w-full" value={intervalHours} onChange={e => setIntervalHours(Number(e.target.value))}>
              {[1, 2, 3, 4, 6, 8, 12].map(h => <option key={h} value={h}>{h} timer</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label className="label">Klokken</label>
            <div className="flex items-center gap-2">
              <select className="filter-input" value={hour} onChange={e => setHour(Number(e.target.value))}>
                {Array.from({ length: 24 }, (_, i) => i).map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}</option>)}
              </select>
              <span className="text-ink-500">:</span>
              <select className="filter-input" value={minute} onChange={e => setMinute(Number(e.target.value))}>
                {[0, 15, 30, 45].map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="btn btn-primary">{saving ? 'Gemmer…' : 'Gem plan'}</button>
        <button onClick={onCancel} className="btn btn-secondary">Annuller</button>
      </div>
    </div>
  );
}
